import { Grid, Stack } from '@mui/material';
import CustomButton from 'components/Common/CustomButton';
import TextInput from 'components/Common/TextInput';
import RolesAutocomplete from 'components/Common/autocomplete/RolesAutocomplete';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { CreateStaff, CreateStaffFormProps, EmployeeType } from 'types/staff';
import { isMerchant } from 'utils/helpers';

const defaultValues = {
    fullName: '',
    email: '',
    employeeType: '',
};

const CreateStaffForm = ({ loading, onSubmit, submitButtonText }: CreateStaffFormProps) => {
    const role = isMerchant();

    const schema = yup.object({
        fullName: yup
            .string()
            .required('Name is required.')
            .matches(/^\S+\s+\S+$/, 'Please enter your full name'),
        email: yup.string().email('Invalid email format').required('Email is required.').lowercase().trim(),
        employeeType: role ? yup.string().required('Access Level is required.') : yup.string(),
    });

    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors },
        trigger,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const onFormSubmit = (formData: CreateStaff) => {
        if (!role) delete formData.employeeType;
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container xs={12} rowGap={{ xs: 2, sm: 3 }} mt={5} textAlign="center" justifyContent="center" alignItems="center">
                <Grid item xs={12} display="flex" justifyContent="center">
                    <Controller
                        name="fullName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ width: { xs: '100%', md: '50%' } }}
                                placeholder="Name"
                                label="Name"
                                icon={null}
                                error={!!errors.fullName}
                                helperText={(errors?.fullName as any)?.message}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="center">
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ width: { xs: '100%', md: '50%' } }}
                                placeholder="Email"
                                label="Email"
                                icon={null}
                                error={!!errors.email}
                                helperText={(errors?.email as any)?.message}
                            />
                        )}
                    />
                </Grid>
                {role && (
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <Controller
                            name="employeeType"
                            control={control}
                            render={({ field: { onChange, ..._field } }) => (
                                <RolesAutocomplete
                                    {..._field}
                                    onChange={(e: any, value: EmployeeType) => {
                                        setValue('employeeType', value);
                                        trigger('employeeType');
                                    }}
                                    sx={{ width: { xs: '100%', md: '50%' } }}
                                    label="Access level"
                                    error={role && !!errors.employeeType}
                                    helperText={(role && (errors?.employeeType as any))?.message}
                                />
                            )}
                        />
                    </Grid>
                )}
            </Grid>
            <Stack mt={5} justifyContent="center" direction="row">
                <CustomButton
                    fontSize={26}
                    title={submitButtonText || 'Send Invite'}
                    type="submit"
                    loading={loading}
                    sx={{ width: { xs: '90%', md: '40%' } }}
                />
            </Stack>
        </form>
    );
};

export default CreateStaffForm;
